import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { useQuery } from 'react-query';
import { Avatar, Button, Select, Spin } from 'antd';
import Link from 'next/link';
import { SearchOutlined, UserOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import type { SearchItem } from '../../shared/utils/search';
import { storageImage } from '../../shared/utils/storage-image';

const CLOSE_MS = 200;

function SearchField({ onNavigate, onCancel }: { onNavigate?: () => void; onCancel?: () => void }) {
  const router = useRouter();
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [debounced] = useDebounce(text.trim(), 300);
  const ready = debounced.length >= 2 && debounced.length <= 80;
  const result = useQuery<SearchItem[]>(['publicSearch', debounced], async ({ signal }) => {
    const response = await fetch(`/api/search?q=${encodeURIComponent(debounced)}`, { signal });
    if (!response.ok) throw new Error('Search unavailable');
    return (await response.json()).results;
  }, { enabled: ready, staleTime: 60_000, retry: 1, refetchOnWindowFocus: false });
  const groups: Record<string, SearchItem[]> = {};
  if (ready && text.trim() === debounced) (result.data || []).forEach(item => {
    (groups[item.group] ||= []).push(item);
  });
  const loading = text.trim().length >= 2 && (text.trim() !== debounced || result.isLoading);

  const error = ready && result.isError && <div role="alert" className="public-search-error">
    <span>{t('searchBox.failed', 'Arama şu anda kullanılamıyor.')}</span>
    <Button size="small" onClick={() => result.refetch()} loading={result.isFetching}>{t('searchBox.retry', 'Tekrar dene')}</Button>
  </div>;

  if (onNavigate) return <>
    <div className="search-sheet-bar">
      <label className="search-sheet-field" htmlFor="mobile-public-search">
        <SearchOutlined aria-hidden="true" />
        <span className="visually-hidden">{t('searchBox.placeholder')}</span>
        <input id="mobile-public-search" type="search" value={text} maxLength={80}
          placeholder={t('searchBox.prompt', 'Ne arıyorsun?')} enterKeyHint="search" autoComplete="off"
          onChange={event => setText(event.target.value)} />
      </label>
      <button type="button" className="search-sheet-cancel" onClick={onCancel}>
        {t('searchBox.cancel', 'Vazgeç')}
      </button>
    </div>
    <div className="search-sheet-results">
      <div role="status" aria-live="polite" className="search-sheet-status">
        {!text.trim() && <p>{t('searchBox.scope', 'Lig, kulüp, kort, turnuva veya oyuncu')}</p>}
        {text.trim().length === 1 && <p>{t('searchBox.minChars', 'En az 2 harf yaz.')}</p>}
        {loading && <><Spin size="small" /><span className="visually-hidden">{t('mobile.loading')}</span></>}
        {ready && !loading && !result.isError && !result.data?.length && <p>{t('searchBox.notFound')}</p>}
      </div>
      {error}
      {Object.entries(groups).map(([group, items]) => <section key={group}>
        <h2>{t(`searchBox.groups.${group}`)}</h2>
        <ul>{items.map(item => <li key={item.value}>
          <Link href={item.value}><a href={item.value} className="public-search-result" onClick={onNavigate}>
            <Avatar
              size={32}
              src={storageImage(item.img, 32)}
              icon={item.isUser ? <UserOutlined /> : <SearchOutlined />}
            />
            <span>{item.text}</span>
          </a></Link>
        </li>)}</ul>
      </section>)}
    </div>
  </>;

  return <>
    <Select<string>
      showSearch
      listHeight={240}
      virtual={false}
      aria-label={t('searchBox.placeholder')}
      filterOption={false}
      value={text || undefined}
      placeholder={t('searchBox.placeholder')}
      onSearch={value => setText(value.slice(0, 80))}
      onSelect={value => {
        setText('');
        router.push(value);
      }}
      notFoundContent={loading ? <Spin size="small" /> : ready && !result.isError ? t('searchBox.notFound') : null}
      style={{ width: '100%' }}
      size="large"
      suffixIcon={<SearchOutlined />}
    >
      {Object.entries(groups).map(([group, items]) => <Select.OptGroup key={group} label={t(`searchBox.groups.${group}`)}>
        {items.map(item => <Select.Option key={item.value} value={item.value}>
          <span className="public-search-result">
            <Avatar
              size={32}
              src={storageImage(item.img, 32)}
              icon={item.isUser ? <UserOutlined /> : <SearchOutlined />}
            />
            <span>{item.text}</span>
          </span>
        </Select.Option>)}
      </Select.OptGroup>)}
    </Select>
    {error}
  </>;
}

SearchField.defaultProps = { onNavigate: undefined, onCancel: undefined };

function SearchSheet({ top, closing, onClose, onClosed }: {
  top: number; closing: boolean; onClose: () => void; onClosed: () => void;
}) {
  const { t } = useTranslation();
  const panel = useRef<HTMLDivElement>(null);

  // Focus inside the same tap so iOS raises the keyboard.
  useLayoutEffect(() => {
    document.getElementById('mobile-public-search')?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    const previous = root.style.overflow;
    root.style.overflow = 'hidden';
    return () => { root.style.overflow = previous; };
  }, []);

  useEffect(() => {
    if (!closing) return undefined;
    const timer = window.setTimeout(onClosed, window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : CLOSE_MS);
    return () => window.clearTimeout(timer);
  }, [closing]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !panel.current) return;
      const focusable = Array.from(panel.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input'));
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!panel.current.contains(document.activeElement)) {
        event.preventDefault();
        first?.focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return <div className={`search-sheet${closing ? ' is-closing' : ''}`}>
    <div className="search-sheet-scrim" aria-hidden="true" onClick={onClose} />
    <div ref={panel} className="search-sheet-panel" role="dialog" aria-modal="true"
      aria-label={t('searchBox.open', 'Ara')}
      style={{ top, '--search-sheet-top': `${top}px` } as React.CSSProperties}>
      <SearchField onNavigate={onClose} onCancel={onClose} />
    </div>
  </div>;
}

export default function SearchBox() {
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [top, setTop] = useState(72);
  const trigger = useRef<HTMLButtonElement>(null);
  const router = useRouter();
  const { t } = useTranslation();

  const show = () => {
    const bottom = trigger.current?.getBoundingClientRect().bottom ?? 64;
    setTop(Math.max(8, Math.round(bottom + 8)));
    setClosing(false);
    setOpen(true);
  };
  const close = () => setClosing(true);
  const closed = () => {
    setOpen(false);
    setClosing(false);
    trigger.current?.focus({ preventScroll: true });
  };

  useEffect(() => {
    if (!open) return undefined;
    router.events.on('routeChangeStart', close);
    return () => router.events.off('routeChangeStart', close);
  }, [open]);

  return <>
    <div className="public-search-desktop"><SearchField /></div>
    <button ref={trigger} type="button" className="public-search-mobile" onClick={show}
      aria-label={t('searchBox.open', 'Ara')} aria-haspopup="dialog" aria-expanded={open && !closing}>
      <SearchOutlined aria-hidden="true" />
    </button>
    {open && <SearchSheet top={top} closing={closing} onClose={close} onClosed={closed} />}
  </>;
}
